<template>
    <BubbleMenu :editor="editor" class="editor__bubble-menu">
        <button
            v-for="item in bubbleItems"
            :key="item.id"
            class="editor__bubble-btn"
            :class="[item.extraClass, {active: item.isActive()}]"
            type="button"
            @click="item.action()"
        >
            <VJoyIcon v-if="item.icon" :name="item.icon" size="xsmall" />
            <component :is="item.labelTag ?? 'span'" v-else>{{ item.label }}</component>
        </button>
    </BubbleMenu>

    <VJoyDialog ref="linkDialog" confirm-text="Insert" cancel-text="Cancel" @dialog:confirm="onLinkConfirm">
        <template #dialog-header>Insert a link</template>
        <template #dialog-body>
            <VJoyInput v-model="linkUrl" name="link-url" placeholder="https://..." />
        </template>
    </VJoyDialog>
</template>

<script setup lang="ts">
    import type {Editor} from '@tiptap/vue-3';
    import {BubbleMenu} from '@tiptap/vue-3/menus';
    import {VJoyDialog, VJoyIcon, VJoyInput} from '@maltjoy/core-vue';
    import {ref} from 'vue';
    import type {JoyIconsId} from '@maltjoy/icons';

    const {editor} = defineProps<{editor: Editor}>();

    const linkDialog = ref<InstanceType<typeof VJoyDialog>>();
    const linkUrl = ref('');

    function onLinkClick() {
        if (editor.isActive('link')) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (editor.chain().focus() as any).unsetLink().run();
            return;
        }
        linkUrl.value = '';
        linkDialog.value?.show();
    }

    function onLinkConfirm() {
        if (linkUrl.value) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (editor.chain().focus() as any).setLink({href: linkUrl.value}).run();
        }
        linkDialog.value?.hide();
    }

    type BubbleItem = {
        id: string;
        label?: string;
        labelTag?: 'strong' | 'em';
        icon?: JoyIconsId;
        extraClass?: string;
        isActive: () => boolean;
        action: () => void;
    };

    const bubbleItems: BubbleItem[] = [
        {
            id: 'bold',
            label: 'B',
            labelTag: 'strong' as const,
            isActive: () => editor.isActive('bold'),
            action: () => editor.chain().focus().toggleBold().run(),
        },
        {
            id: 'italic',
            label: 'i',
            labelTag: 'em' as const,
            isActive: () => editor.isActive('italic'),
            action: () => editor.chain().focus().toggleItalic().run(),
        },
        {
            id: 'h2',
            label: 'T',
            extraClass: 'editor__bubble-btn--h2',
            isActive: () => editor.isActive('heading', {level: 2}),
            action: () => editor.chain().focus().toggleHeading({level: 2}).run(),
        },
        {
            id: 'h3',
            label: 'T',
            extraClass: 'editor__bubble-btn--h3',
            isActive: () => editor.isActive('heading', {level: 3}),
            action: () => editor.chain().focus().toggleHeading({level: 3}).run(),
        },
        {
            id: 'link',
            icon: 'link',
            isActive: () => editor.isActive('link'),
            action: onLinkClick,
        },
    ];
</script>

<style lang="scss" scoped>
    .editor__bubble-menu {
        display: flex;
        background: var(--joy-color-neutral-0);
        border: 1.5px solid var(--joy-color-secondary-50);
        border-radius: var(--joy-core-radius-4);
        overflow: hidden;
        box-shadow: var(--joy-core-elevation-2);
    }

    .editor__bubble-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 36px;
        background: none;
        border: none;
        border-right: 1px solid var(--joy-color-neutral-20);
        cursor: pointer;
        color: var(--joy-color-text-body);
        font-size: var(--joy-font-size-primary-500);
        font-family: inherit;
        transition: background var(--joy-transition-duration-short);

        &:last-child {
            border-right: none;
        }

        &:hover {
            background: var(--joy-color-neutral-10);
        }

        &.active {
            background: var(--joy-color-secondary-10);
            color: var(--joy-color-secondary-50);
        }

        &--h2 {
            font-size: var(--joy-font-size-primary-600);
            font-weight: var(--joy-font-weight-bold);
        }

        &--h3 {
            font-size: var(--joy-font-size-primary-400);
            font-weight: var(--joy-font-weight-bold);
        }
    }
</style>
